import {
    DateFormats,
    formatDate,
    formatNameFirstLast,
    formatNameLastFirst,
} from '@strata/helper-functions';
import dayjs from 'dayjs';

import { CLIENT_API_URL, fmhNpiAPI } from 'api/APIBase';
import { getReferentCode } from 'api/referenceAPI';
import { DIRECTIONS_MANUAL_ENTRY, RadioOptions } from 'services/constants';
import { handleError } from 'services/Error';

/** Submits a new referral */
export async function submitReferral(capturedData, user) {
    // Fetch referent product service code
    let referentProductServiceCode;
    try {
        referentProductServiceCode = (
            await getReferentCode(
                capturedData.productServiceOid,
                capturedData.billing.toLowerCase(),
            )
        )?.code;
    } catch (err) {
        handleError('An error occurred retrieving referent product service code.', err);
        throw err;
    }

    const referral = {
        referral_id: capturedData.referralId,
        // Prescriber & patient
        prescriber_npi: capturedData.prescriberNpi,
        prescriber_name: capturedData.prescriberName,
        prescriber_phone: capturedData.prescriberPhone,
        patient_name: formatNameFirstLast(
            capturedData.memberFirstName,
            capturedData.memberLastName,
        ),
        patient_dob: formatDate(capturedData.memberDOB, DateFormats.HYPHEN_DATE),
        member_age: capturedData.memberAge,
        member_city: capturedData.memberCity,
        member_state: capturedData.memberState,
        member_zip: capturedData.memberZip,
        payer_id: capturedData.payerId,
        segment_id: capturedData.segmentId,
        member_id: capturedData.memberId,
        // Medication & diagnosis
        product_service_oid: capturedData.productServiceOid,
        product_service_id: referentProductServiceCode,
        product_days: 30, // Hard-coding product days to 30, may need to be revisited
        product_service_display_name: capturedData.medication,
        diagnosis_oid: capturedData.diagnosisException?.diagnosis?.oid ?? capturedData.diagnosisOid,
        diagnosis_code:
            capturedData.diagnosisException?.diagnosis?.code ?? capturedData.diagnosisCode,
        // Prescribing information
        billing: capturedData.billing,
        rx_sig:
            capturedData.directionOption === DIRECTIONS_MANUAL_ENTRY
                ? capturedData.manualDirectionText
                : capturedData.directionOption,
        strength: `${capturedData.strength} ${capturedData.strengthUom}`,
        dosage: capturedData.dosage,
        product_qty: capturedData.quantity,
        rx_refillcount: capturedData.refills,
        daw_code: capturedData.dawCode,
        special_instructions: capturedData.specialInstructions,
        // Place of Service & Delivery
        place_of_service: capturedData.placeOfService,
        facility_npi: capturedData.facilityNpi,
        practice_npi: capturedData.practiceNpi,
        // Exception Rationales
        exception_rationale:
            capturedData.diagnosisException?.rationale ??
            capturedData.clinicalPolicyException?.rationale,
        site_of_care_exception_rationale: capturedData.siteOfCareException?.rationale,
        clinical_info: JSON.stringify({
            clinicalPolicyAnswers: capturedData.clinicalPolicyAnswers,
            additionalMedications: capturedData.additionalMedications,
            needsByDate: capturedData.needsByDate,
            pointOfContact: capturedData.pointOfContact,
            pointOfContactPhone: capturedData.pointOfContactPhone,
            pointOfContactFax: capturedData.pointOfContactFax,
        }),
        sent_to_pharmacy:
            capturedData.sentToPharmacy === RadioOptions.YES
                ? JSON.stringify({
                      npi: capturedData.sentToPharmacyNpi,
                      name: capturedData.sentToPharmacyName,
                      address: capturedData.sentToPharmacyAddress,
                      reason: capturedData.sentToPharmacyReason,
                  })
                : undefined,
        submitted_by: formatNameLastFirst(user.family_name, user.given_name),
        submitted_at: dayjs().toISOString(),
        health_plan: capturedData.segmentName,
        renews_referral_id: capturedData.renewsReferralId,
    };

    // Save clinical info for referral
    const url = `${CLIENT_API_URL}/${referral.payer_id}/clinical`;
    await fmhNpiAPI({ url, npi: referral.prescriber_npi }).post(referral).json();
}
